import { useState } from 'react';
import { Fragment } from 'react/jsx-runtime';

import {
  ActionIcon,
  Button,
  Divider,
  Group,
  NumberInput,
  Select,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { closeModal } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconMinus, IconPlus } from '@tabler/icons-react';
import { dateToHourString } from '@utils/date';
import { handleSubmitError } from '@utils/forms';
import { mapWeekDay } from '@utils/mappers';

import {
  CreateTherapyDrugRequest,
  TherapyDrug,
  UpdateTherapyDrugRequest,
} from '@interfaces/therapies.interface';

import {
  useCreateTherapyDrugMutation,
  useUpdateTherapyDrugMutation,
} from '@api/therapies.api';

import DrugsSelector from '@components/drugs/DrugsSelector';

export const UPSERT_THERAPY_DRUG_FORM_MODAL_ID = 'upsertTherapyDrug';

interface UpsertTherapyDrugFormProps {
  therapyId: string;
  therapyDrug?: TherapyDrug;
}

type PosologyPreset = 'twicePerDay' | 'thricePerDay' | 'manual';

export default function UpsertTherapyDrugForm({
  therapyId,
  therapyDrug,
}: UpsertTherapyDrugFormProps) {
  const [posologyPreset, setPosologyPreset] = useState<
    PosologyPreset | undefined
  >(therapyDrug ? 'manual' : undefined);
  const [posologyQuantityPreset, setPosologyQuantityPreset] =
    useState<number>();

  const initialValues = {
    drugAic: therapyDrug?.drug.aic || '',
    drugMeasureUnit: therapyDrug?.drugMeasureUnit,
    posology: therapyDrug?.posology
      ? therapyDrug.posology.map((posology) => ({
          ...posology,
          hour: dateToHourString(new Date(posology.hour)) as string | undefined,
          quantity: posology.quantity as number | undefined,
        }))
      : [],
  };

  const form = useForm({
    initialValues,
  });

  const [createTherapyDrug, { isLoading: isLoadingCreate }] =
    useCreateTherapyDrugMutation();
  const [updateTherapyDrug, { isLoading: isLoadingUpdate }] =
    useUpdateTherapyDrugMutation();

  const onCancelClick = () => {
    closeModal(UPSERT_THERAPY_DRUG_FORM_MODAL_ID);
  };

  const onSubmit = async (values: typeof initialValues) => {
    try {
      const mappedValues = {
        drugAic: values.drugAic,
        drugMeasureUnit: values.drugMeasureUnit!,
        posology: values.posology.map((posology) => {
          const hourSplitted = posology.hour!.split(':');

          return {
            dayOfWeek: posology.dayOfWeek,
            hour: new Date(
              0,
              0,
              0,
              +hourSplitted[0],
              +hourSplitted[1],
              0,
            ).toISOString(),
            quantity: posology.quantity!,
          };
        }),
      };

      if (therapyDrug) {
        const data: UpdateTherapyDrugRequest = {
          therapyId,
          drugAic: therapyDrug.drug.aic,
          body: mappedValues,
        };

        await updateTherapyDrug(data).unwrap();

        notifications.show({
          title: 'Farmaco terapia modificato',
          message: 'Farmaco terapia modificato con successo.',
        });
      } else {
        const data: CreateTherapyDrugRequest = {
          therapyId,
          body: mappedValues,
        };

        await createTherapyDrug(data).unwrap();

        notifications.show({
          title: 'Farmaco terapia aggiunto',
          message: 'Farmaco terapia aggiunto con successo.',
        });
      }

      closeModal(UPSERT_THERAPY_DRUG_FORM_MODAL_ID);
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  const handlePosologyPresetChange = (value: PosologyPreset) => {
    setPosologyPreset(value);
    setPosologyQuantityPreset(undefined);

    switch (value) {
      case 'twicePerDay':
        form.setFieldValue(
          'posology',
          Array.from({ length: 7 }, (_, i) => [
            {
              dayOfWeek: i + 1,
              hour: '08:00',
              quantity: 0, // Temp quantity
            },
            {
              dayOfWeek: i + 1,
              hour: '20:00',
              quantity: 0, // Temp quantity
            },
          ]).flat(),
        );
        break;
      case 'thricePerDay':
        form.setFieldValue(
          'posology',
          Array.from({ length: 7 }, (_, i) => [
            {
              dayOfWeek: i + 1,
              hour: '08:00',
              quantity: 0, // Temp quantity
            },
            {
              dayOfWeek: i + 1,
              hour: '16:00',
              quantity: 0, // Temp quantity
            },
            {
              dayOfWeek: i + 1,
              hour: '22:00',
              quantity: 0, // Temp quantity
            },
          ]).flat(),
        );
        break;
      case 'manual':
        form.setFieldValue('posology', []);
        break;
    }
  };

  const handlePosologyQuantityPresetChange = (value: number) => {
    setPosologyQuantityPreset(value);

    form.setFieldValue(
      'posology',
      form.values.posology.map((posology) => ({
        ...posology,
        quantity: value,
      })),
    );
  };

  const handleAddPosologyClick = (dayOfWeek: number) => {
    form.insertListItem('posology', {
      dayOfWeek,
      hour: undefined,
      quantity: undefined,
    });
    setPosologyPreset('manual');
    setPosologyQuantityPreset(undefined);
  };

  const handleRemovePosologyClick = (posologyIndex: number) => {
    form.removeListItem('posology', posologyIndex);
    setPosologyPreset('manual');
    setPosologyQuantityPreset(undefined);
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm">
        {!therapyDrug && (
          <DrugsSelector
            label="Farmaco"
            {...form.getInputProps('drugAic')}
            required
            disabled={isLoadingCreate || isLoadingUpdate}
          />
        )}

        <Select
          label="Unità di misura"
          placeholder="Seleziona unità di misura"
          data={[
            { label: 'Millilitri (ml)', value: 'ml' },
            { label: 'Compresse (cpr)', value: 'cpr' },
            { label: 'Capsule (cps)', value: 'cps' },
            { label: 'Gocce (gtt)', value: 'gtt' },
            { label: 'Fiale (fl)', value: 'fl' },
          ]}
          {...form.getInputProps('drugMeasureUnit')}
          required
          disabled={isLoadingCreate || isLoadingUpdate}
        />

        <Text fw={500}>Posologia</Text>

        {!therapyDrug && (
          <Select
            placeholder="Seleziona posologia"
            data={[
              { label: '2 volte al giorno', value: 'twicePerDay' },
              { label: '3 volte al giorno', value: 'thricePerDay' },
              { label: 'Manuale', value: 'manual' },
            ]}
            required
            onChange={(value) =>
              handlePosologyPresetChange(value as PosologyPreset)
            }
            value={posologyPreset}
            disabled={isLoadingCreate || isLoadingUpdate}
          />
        )}

        {posologyPreset && posologyPreset !== 'manual' && (
          <NumberInput
            label="Quantità"
            mb="md"
            required
            min={0}
            value={posologyQuantityPreset || ''}
            onChange={(value) => handlePosologyQuantityPresetChange(+value)}
            disabled={isLoadingCreate || isLoadingUpdate}
          />
        )}

        {posologyPreset &&
          (posologyQuantityPreset !== undefined ||
            posologyPreset === 'manual') &&
          Array.from({ length: 7 }).map((_, index) => (
            <Fragment key={index}>
              <Group>
                <Divider
                  label={mapWeekDay(index + 1)}
                  labelPosition="left"
                  styles={{
                    root: {
                      flex: 1,
                    },
                    label: {
                      fontSize: 'var(--mantine-font-size-sm)',
                      color: 'var(--mantine-color-black)',
                    },
                  }}
                />
                <Tooltip label="Aggiungi somministrazione">
                  <ActionIcon onClick={() => handleAddPosologyClick(index + 1)}>
                    <IconPlus />
                  </ActionIcon>
                </Tooltip>
              </Group>
              {form.values.posology.filter(
                (posology) => posology.dayOfWeek === index + 1,
              ).length === 0 && (
                <Text fs="italic" size="sm" c="brand">
                  Nessuna somministrazione
                </Text>
              )}
              {form.values.posology
                .filter((posology) => posology.dayOfWeek === index + 1)
                .map((posology) => {
                  const posologyIndex = form.values.posology.indexOf(posology);

                  return (
                    <Group key={posologyIndex} align="end">
                      <TimeInput
                        flex={1}
                        label="Orario"
                        required
                        {...form.getInputProps(
                          `posology.${posologyIndex}.hour`,
                        )}
                        onChange={(e) => {
                          form.setFieldValue(
                            `posology.${posologyIndex}.hour`,
                            e.target.value,
                          );
                          setPosologyPreset('manual');
                          setPosologyQuantityPreset(undefined);
                        }}
                      />
                      <NumberInput
                        flex={1}
                        label="Quantità"
                        required
                        min={0}
                        {...form.getInputProps(
                          `posology.${posologyIndex}.quantity`,
                        )}
                        onChange={(value) => {
                          form.setFieldValue(
                            `posology.${posologyIndex}.quantity`,
                            +value,
                          );
                          setPosologyPreset('manual');
                          setPosologyQuantityPreset(undefined);
                        }}
                      />
                      <Tooltip label="Rimuovi somministrazione">
                        <ActionIcon
                          color="red"
                          onClick={() =>
                            handleRemovePosologyClick(posologyIndex)
                          }
                        >
                          <IconMinus />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  );
                })}
            </Fragment>
          ))}
      </Stack>

      {form.errors.general && (
        <Text c="red" size="xs" ta="right">
          {form.errors.general}
        </Text>
      )}

      <Group justify="right" mt="xl">
        <Button
          onClick={onCancelClick}
          variant="default"
          loading={isLoadingCreate || isLoadingUpdate}
        >
          Annulla
        </Button>
        <Button type="submit" loading={isLoadingCreate || isLoadingUpdate}>
          Conferma
        </Button>
      </Group>
    </form>
  );
}
